/**
 * ProductCheckoutPage — single dynamic route (/checkout/:productId) that
 * renders the hero image, description, price, and a Buy Now CTA for any
 * product in the catalog. When Stripe is wired on the backend, the CTA
 * will hit `/api/checkout/create-session` with the product id and redirect
 * to the returned Stripe URL. Until then it shows a "Coming Soon" toast.
 */
import React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle2, ShoppingBag, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { PRODUCT_BY_ID, PRODUCTS } from '../data/products';
import ProductLiveDemo from '../components/ProductLiveDemo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductCheckoutPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const product = PRODUCT_BY_ID[productId];
  const [busy, setBusy] = React.useState(false);
  // Buyers who don't already have a completed RV Match should still get a
  // "My Tools" dashboard after purchase — we need their email at checkout so
  // the /my/purchases lookup can find them by email later. Pre-fill from
  // localStorage if a returning buyer.
  const [email, setEmail] = React.useState(() => {
    try { return localStorage.getItem('rvm:buyer_email') || ''; } catch { return ''; }
  });

  if (!product) {
    return (
      <div className="min-h-screen bg-[#05070d] text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-black mb-2">Product not found</h1>
          <p className="text-slate-400 mb-6">The product you're looking for doesn't exist.</p>
          <Link to="/" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-amber-400 text-black font-bold">
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  const handleBuy = async () => {
    const trimmed = (email || '').trim().toLowerCase();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      toast.error('Enter the email where we should send your purchase.');
      return;
    }
    setBusy(true);
    try {
      localStorage.setItem('rvm:buyer_email', trimmed);
      const res = await axios.post(`${API}/payments/checkout`, {
        product_id: product.id,
        origin_url: window.location.origin,
        email: trimmed,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      toast.error('Checkout is temporarily unavailable. Please try again in a moment.');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not open checkout. Please try again.';
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05070d] text-white" data-testid={`checkout-page-${product.id}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6 transition-colors"
          data-testid="checkout-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 items-start">
          {/* Hero art */}
          <div className="rounded-3xl overflow-hidden ring-1 ring-white/10 bg-white/[0.02]">
            {product.image ? (
              <img
                src={product.image}
                alt={product.name}
                className="w-full aspect-square object-contain bg-[#05070d]"
              />
            ) : (
              <div className="w-full aspect-square bg-gradient-to-br from-amber-500/20 to-transparent flex items-center justify-center text-white/40 uppercase tracking-widest text-sm font-bold">
                {product.name}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            {product.highlight && (
              <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400 text-[#05070d] text-[10px] font-black tracking-widest mb-3">
                MOST POPULAR
              </div>
            )}
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-3">
              {product.name}
            </h1>

            {/* Tagline + savings badge — surfaces `product.tagline` and
                `product.savingsLabel` so bundles clearly advertise the
                "5 tools · Save $34.96" hook right under the H1. */}
            {(product.tagline || product.savingsLabel) && (
              <div className="flex items-center gap-2 flex-wrap mb-4">
                {product.tagline && (
                  <p className="text-sm font-bold text-amber-300" data-testid="product-tagline">
                    {product.tagline}
                  </p>
                )}
                {product.savingsLabel && (
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 text-[11px] font-black tracking-widest"
                    data-testid="product-savings-badge"
                  >
                    {product.savingsLabel}
                  </span>
                )}
              </div>
            )}

            <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-6">
              {product.description}
            </p>

            {product.includes && (
              <div className="mb-6">
                <p className="text-xs font-bold tracking-widest text-amber-300 mb-3">
                  INCLUDES ALL {product.includes.length} TOOLS
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                  {product.includes.map((inc) => {
                    const child = PRODUCT_BY_ID[inc];
                    return (
                      <li key={inc} className="flex items-start gap-2 text-sm text-slate-200">
                        <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-amber-300" />
                        <span>{child ? child.name : inc.replace(/_/g, ' ')}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {product.features && (
              <div className="mb-6">
                <p className="text-xs font-bold tracking-widest text-fuchsia-300 mb-3">WHAT YOU GET</p>
                <ul className="space-y-2">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-200">
                      <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5 text-fuchsia-300" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Interactive "Example in Action" — hands-on demo of this exact
                tool the buyer is about to pay for. Silently no-op for
                products without a demo (currently: none). */}
            <ProductLiveDemo productId={product.id} />

            {/* Email input — required so we can email the receipt AND set
                up a purchase-only /my-matches dashboard for buyers who
                haven't completed the RV Match form yet. */}
            <label className="block mb-3 mt-6">
              <span className="block text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1.5">
                Email for receipt & access
              </span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                inputMode="email"
                data-testid="checkout-email"
                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/10 focus:border-amber-400 focus:outline-none text-white placeholder-slate-500"
              />
            </label>

            <button
              type="button"
              onClick={handleBuy}
              disabled={busy}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-amber-400 hover:bg-amber-300 text-[#05070d] font-black text-base shadow-[0_10px_40px_-10px_rgba(245,158,11,0.6)] transition-shadow disabled:opacity-60"
              data-testid="checkout-buy-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
              Buy Now — {product.priceLabel}
            </button>

            <p className="text-xs text-slate-500 mt-3">
              Secure checkout · Delivered instantly by email · All sales are final
            </p>
          </div>
        </div>

        {/* Cross-sell: every OTHER consumer product the buyer could add. Keeps
            them on the site instead of forcing a back-arrow to the homepage.
            Dealer products are hidden from this row — buyers should never see
            dealer-facing offers. */}
        <OtherProducts currentId={product.id} />
      </div>
    </div>
  );
};

// -------- OtherProducts (consumer-only cross-sell row) --------
const OtherProducts = ({ currentId }) => {
  const others = PRODUCTS.filter((p) => p.id !== currentId && p.category !== 'dealer' && !p.hiddenFromCatalog);
  if (others.length === 0) return null;
  return (
    <section className="mt-12 sm:mt-16" data-testid="checkout-cross-sell">
      <div className="mb-5 flex items-baseline justify-between gap-3">
        <div>
          <p className="text-xs font-bold tracking-[0.25em] text-cyan-300 mb-1">KEEP GOING</p>
          <h2 className="text-xl sm:text-2xl font-black">Also from the RV Match AI toolkit</h2>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {others.map((p) => (
          <Link
            key={p.id}
            to={p.href}
            data-testid={`cross-sell-${p.id}`}
            className="group rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden hover:border-white/20 transition-colors"
          >
            {p.image ? (
              <div className="aspect-square w-full bg-[#05070d] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                />
              </div>
            ) : (
              <div className="aspect-square w-full bg-gradient-to-br from-white/5 to-transparent flex items-center justify-center text-white/40 text-xs uppercase tracking-widest font-bold px-3 text-center">
                {p.name}
              </div>
            )}
            <div className="p-3 sm:p-4">
              <div className="flex items-baseline justify-between gap-2 mb-1">
                <h3 className="text-sm sm:text-base font-bold text-white leading-tight line-clamp-2">{p.name}</h3>
                <span className="shrink-0 text-xs sm:text-sm font-black text-white/90">{p.priceLabel}</span>
              </div>
              <p className="text-xs text-slate-400 mb-2 line-clamp-2">{p.tagline}</p>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 group-hover:text-white transition-colors">
                View <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
              </span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default ProductCheckoutPage;
